import constants from '../constants/jsonXMLConstants';
import { extractFCNData } from '../components/jsonEditor/additionalNodes'
import { getOrCreateSpecialClaim, JE_technicalProfileClaimRef } from './jsonFQContext';
import {
  BuildSettings,
  extractSettingFieldValue,
  extractTemplatizableField,
  getKeyedTemplatizableValue,
  getKeyedValue,
  getNextValidID,
  isRealResolver,
  isSpecialResolver,
  parseToBoolean,
  sanitizeDisplayName,
  TemplatizationInfo,
} from './utils';
import settings from '../constants/jsonXMLsettings';
import jsonConstants from '../constants/jsonConstants';
import jsonXMLConstants from '../constants/jsonXMLConstants';
import { generateGetClaimsMethod, generateMakeTransformMethod, JsonClaimTransformData } from './mappedClaimTransforms/mappedClaimTransforms';

export interface JsonFlowchartNode {
  nodeId: string;
  displayName: string;
  sanitizedDisplayName: string;
  kind: string; // 'signIn', 'aadRead', 'aadWrite', 'restAPI', 'usernamePasswordValidation', 'conditional', 'variableAssignment', 'userInput', 'mfa', 'tokenPage'
  isLink: boolean;
  data: JE_NodeData;
  children: string[];
  parents: string[];
  linkType?: string;
  childBranchVars: (string | null)[];
  sessionBehavior?: JE_NodeSessionBehavior;
}

export interface JE_FlowchartIndex {
  nodes: { [nodeId: string]: JsonFlowchartNode };
  root: string;
  type: string;
  name: string;
  parentFlow?: string;
}

export interface JE_SignIn_LinkCollection {
  forgotPassword: boolean;
  otp: boolean;
  createNewAccount: boolean;
  employeeLogin: boolean;
  socialProviders: { [index: string]: boolean };
  customLinks: string[];
}

const collectionOpNames = {
  insert: 'append',
  replace: 'replace',
  remove: 'remove',
};

export interface JE_AAD_ClaimMapping {
  claimId: string;
  attributeId: string;
  defaultValue?: any;
  alwaysUseDefaultValue?: boolean;
  collectionOp?: string; // 'append' | 'remove' | 'replace'
  concatIndex?: number;
  claimResolver?: boolean;
  specialSessionContext?: boolean;
}

export interface JE_UserInput_InputField {
  kind: string;
  claimId: string;
  readonly: boolean;
  prefilled: string;
  prefill_source: string;
  required: boolean;
}

export interface JE_Conditional_Condition {
  claimSrc: string;
  operator: string;
  compareTo: string;
  compareValue: string | boolean;
  ignoreCase?: boolean;
  dateTimeOffset?: number;
}

export interface JE_Conditional_ConditionGroup {
  conditions: JE_Conditional_Condition[];
}

export interface JE_SignInNodeData {
  links: JE_SignIn_LinkCollection;
  userNameType: string;
  userNameClaim: string;
  keepMeSignedIn: boolean;
  linkCount: number;
  collectPassword: boolean;
  generateLandingCEX: boolean;
  contentDefinitionUrl?: string;
  formSubmitAction?: string;
  validationErrorMessage?: string;
}

export interface JE_AADReadNodeData {
  objectIndex: string;
  objectIndexAttribute: JE_AAD_ClaimMapping;
  claimAttributeMap: JE_AAD_ClaimMapping[];
  throwErrorIfNotFound: boolean;
  errorMessage: string;
}

export interface JE_AADWriteNodeData {
  objectIndex: string;
  throwErrorIfNotFound: boolean;
  errorMessage: string;
  throwErrorIfExists: boolean;
  isCreateNew: boolean;
  claimAttributeMap: JE_AAD_ClaimMapping[];
  inputConcatenations: [string, string][];
  objectIdOutputClaim?: string;
  upnOutputClaim?: string;
}

const userInputFieldPrefillValues: { [index: string]: string } = {
  defaultValue: 'literal',
  fromAnotherAttribute: 'claim',
  sessionContext: 'resolver',
  none: 'none',
};

export interface JE_UserInput_EmailValidation {
  emailClaimId: string;
  prefillValue: boolean;
  prefillSource?: string;
  prefillSourceType: string;
  readonly: boolean;
  expiryTime: number;
  sendAction?: string;
}

export interface JE_UserInputNodeData {
  inputFields: JE_UserInput_InputField[];
  emailValidation?: JE_UserInput_EmailValidation;
  specialPasswordValidation?: boolean; // TODO: Find a cleaner way to do this. Currently flags if newPassword/reenterPassword were generated out of 'password' so that 'password' will be made available in the VTP.
  contentDefinitionUrl?: string;
  formSubmitAction?: string;
  validationErrorMessage?: string;
}

export interface JE_AppInsights_InputField {
  claimId: string;
  partnerClaimType: string;
  defaultValue: string;
  // alwaysUseDefaultValue: boolean;
}

export interface JE_AppInsightsNodeData {
  inputFields: JE_AppInsights_InputField[];
  instrumentationKey: string;
  eventType: string;
}

export interface JE_IdPClaimMapping {
  partnerClaim: string;
  localClaim: string;
}

export interface JE_IdPNodeData {
  clientSecretRef: string;
  clientId: string;
  partnerClaimMappings: JE_GenericIdPClaimMapping[];
}

export interface JE_ConditionalNodeData {
  conditionGroups: JE_Conditional_ConditionGroup[];
}

export interface JE_UsernamePasswordValidationNodeData {
  userIdType: string;
  userIdClaim: string;
  passwordClaim: string;
  objectIdClaim: string;
  clientId: string;
  resourceId: string;
  errorMessage: string;
}

export interface JE_PasswordComplexityRequirements {
  lowercaseLetters: boolean;
  uppercaseLetters: boolean;
  letters: boolean;
  numbers: boolean;
  specialCharacters: boolean;
  numberIncluded?: number;
  minimumLength?: number;
  maximumLength?: number;
}

export interface JE_EndpointRef {
  id: string;
  journeyId: string;
}

export interface JE_TokenIssuerClaimRef {
  claimId: string;
  defaultValue?: string;
  defaultValueSource?: 'literal' | 'resolver';
  partnerClaimType?: string;
}

export interface JE_TokenIssuerNodeData {
  claims: JE_TokenIssuerClaimRef[];
  tokenIssuerType: string;
  issuerUri?: string;
  ssoScope: string;
  ssoExpiryType: string;
  ssoExpiryTime: string;
  endpoints: JE_EndpointRef[];
}

export interface JE_RESTNodeParameter {
  sourceValue: string;
  sourceType: 'claim' | 'resolver';
  partnerClaimType: string;
  defaultValue?: string;
  alwaysUseDefaultValue?: boolean;
}

export interface JE_RESTNodeData {
  authType: string;
  serviceURL: string;
  sendClaimsIn: string;
  inputParameters: JE_RESTNodeParameter[];
  outputParameters: JE_RESTNodeParameter[];
  clientIdContainerName?: string;
  clientSecretContainerName?: string;
  issuerTenant?: string;
  applicationIdUri?: string;
  clientCertContainerName?: string;
}

export type JE_MFAPhoneNumberEntryMode = 'alwaysNew' | 'newIfNotPresent' | 'existingOnly';

export interface JE_MFANodeData {
  idClaimId: string;
  phoneNumberInputClaimId: string;
  phoneNumberOutputClaimId: string;
  newlyEnteredFlagClaimId: string;
  numberEntryMode: JE_MFAPhoneNumberEntryMode;
  contentDefinitionUrl?: string;
}

export interface JE_NodeSessionBehavior {
  sessionAction: string; // skipInAnySession | always | skipIfExecuted | social | tokenIssuer
  sessionId: string;
}

export interface JE_WebAuthnRegistrationNodeData {
  storageApiUrl: string;
  credentialApiUrl: string;
  idClaim: string;
  nameClaim: string;
  displayNameClaim: string;
  successClaim: string;
  contentDefinitionUrl?: string;
}

export interface JE_WebAuthnAuthenticationNodeData {
  storageApiUrl: string;
  credentialApiUrl: string;
  idClaim: string;
  successClaim: string;
  contentDefinitionUrl?: string;
}

export interface JE_WebAuthnCredentialExistsNodeData {
  existsClaim: string;
  contentDefinitionUrl?: string;
}

export interface JE_WebAuthnAvailabilityNodeData {
  availabilityClaim: string;
  requirePlatformAuth: boolean;
  contentDefinitionUrl?: string;
}

export interface JE_ConditionalAccessEvaluationNodeData {
  idClaim: string;
  mfaRegisteredClaim: string;
  changePasswordFlagClaim: string;
  mfaFlagClaim: string;
  blockFlagClaim: string;
}

export type JE_ErrorPageSourceType = 'literal' | 'claim' | 'resolver';

export interface JE_ErrorPageEntry {
  sourceType: JE_ErrorPageSourceType;
  source: string;
}

export interface JE_ErrorPageNodeData {
  entries: JE_ErrorPageEntry[];
  contentDefinitionUrl?: string;
}

export interface JE_AuthorizeTokenNodeData {
  claims: JE_technicalProfileClaimRef[];
  issuer: string;
  audience: string[];
  issuerSecret: string;
}

export interface JE_UserInfoIssuerNodeData {
  claims: JE_technicalProfileClaimRef[];
  issuerSecret: string;
}

export type JE_SendEmailOTPSourceType = 'literal' | 'claim' | 'resolver';

export interface JE_SendEmailOTPField {
  sourceType: string;
  source: string;
  templateField: string;
}

export interface JE_SendEmailOTPLanguageSetting {
  sourceType: string;
  source: string;
}

export interface JE_SendEmailOTPNodeData {
  otpField: string;
  emailClaim: string;
  templateName: string;
  language: JE_SendEmailOTPLanguageSetting;
  fields: JE_SendEmailOTPField[];
}

export type JE_OIDCResponseType = 'code' | 'id_token';
export type JE_OIDCResponseMode = 'form_post' | 'query';
export type JE_OIDCScope = 'profile' | 'email' | 'address' | 'phone'
export interface JE_GenericIdPClaimMapping {
  claimId: string;
  partnerClaimType: string;
  defaultValue?: string;
  alwaysUseDefaultValue?: boolean;
}

export interface JE_OIDCIdPNodeData {
  idpDomain: string;
  metadataUrl: string;
  authEndpointUrl: string;
  endSessionEndpointUrl: string;
  clientId: string;
  responseType: JE_OIDCResponseType;
  clientSecretContainerName: string;
  responseMode: JE_OIDCResponseMode;
  issuerIdPartnerClaimType: string;
  forceScopes: JE_OIDCScope[];
  inputClaims: JE_GenericIdPClaimMapping[];
  outputClaims: JE_GenericIdPClaimMapping[];
}

export interface JE_SAMLIdPNodeData {
  idpDomain: string;
  metadataUrl: string;
  signRequests: boolean;
  messageSigningKey: string;
  signatureAlgorithm: 'Sha1' | 'Sha256' | 'Sha384' | 'Sha512';
  expectResponseSignatures: boolean;
  encryptAssertions: boolean;
  assertionDecryptionKey: string;
  metadataSigningKey: string;
  requestUserClaim?: string;
  issuerIdPartnerClaimType: string;
  outputClaims: JE_GenericIdPClaimMapping[];
}

type JE_SetClaimsSourceType = 'claim' | 'literal' | 'resolver' | 'transform';

export interface JE_SetClaimsAssignmentData {
  claimToSet: string;
  sourceType: JE_SetClaimsSourceType;
  sourceValue: string;
  defaultValue: string;
  transformData?: JsonClaimTransformData;
}

export interface JE_SetClaimsNodeData {
  claimsToSet: JE_SetClaimsAssignmentData[];
}

export type JE_NodeData =
  | JE_SignInNodeData
  | JE_AADReadNodeData
  | JE_AADWriteNodeData
  | JE_UserInputNodeData
  | JE_ConditionalNodeData
  | JE_IdPNodeData
  | JE_UsernamePasswordValidationNodeData
  | JE_TokenIssuerNodeData
  | JE_RESTNodeData
  | JE_MFANodeData
  | JE_WebAuthnRegistrationNodeData
  | JE_WebAuthnAuthenticationNodeData
  | JE_WebAuthnCredentialExistsNodeData
  | JE_WebAuthnAvailabilityNodeData
  | JE_ConditionalAccessEvaluationNodeData
  | JE_ErrorPageNodeData
  | JE_AuthorizeTokenNodeData
  | JE_UserInfoIssuerNodeData
  | JE_SendEmailOTPNodeData
  | JE_OIDCIdPNodeData
  | JE_SAMLIdPNodeData
  | JE_SetClaimsNodeData
  | {};

const nodeComponentTypes: { [index: string]: string } = {
  [constants.editorComponentTypes.conditional]: constants.componentTypes.conditional,
  [constants.editorComponentTypes.signIn]: constants.componentTypes.signIn,
  [constants.editorComponentTypes.userInput]: constants.componentTypes.userInput,
  [constants.editorComponentTypes.aadRead]: constants.componentTypes.aadRead,
  [constants.editorComponentTypes.aadUpdate]: constants.componentTypes.aadWrite,
  [constants.editorComponentTypes.aadCreate]: constants.componentTypes.aadWrite,
  [constants.editorComponentTypes.usernamePasswordValidation]: constants.componentTypes.usernamePasswordValidation,
  [constants.editorComponentTypes.tokenIssuer]: constants.componentTypes.tokenIssuer,
  social: constants.componentTypes.idp,
  [constants.editorComponentTypes.rest]: constants.componentTypes.rest,
  [constants.editorComponentTypes.mfa]: constants.componentTypes.mfa,
  [constants.editorComponentTypes.google]: constants.componentTypes.google,
  [constants.editorComponentTypes.webAuthnRegistration]: constants.componentTypes.webAuthnRegistration,
  [constants.editorComponentTypes.webAuthnAuthentication]: constants.componentTypes.webAuthnAuthentication,
  [constants.editorComponentTypes.webAuthnCredentialsExist]: constants.componentTypes.webAuthnCredentialExists,
  [constants.editorComponentTypes.webAuthnAvailability]: constants.componentTypes.webAuthnAvailability,
  [constants.editorComponentTypes.conditionalAccessEvaluation]: constants.componentTypes.conditionalAccessEvaluation,
  [constants.editorComponentTypes.conditionalAccessRemediation]: constants.componentTypes.conditionalAccessRemediation,
  [constants.editorComponentTypes.errorPage]: constants.componentTypes.errorPage,
  [constants.editorComponentTypes.authorizeToken]: constants.componentTypes.authorizeToken,
  [constants.editorComponentTypes.userInfoIssuer]: constants.componentTypes.userInfoIssuer,
  [constants.editorComponentTypes.appInsights]: constants.componentTypes.appInsights,
  [constants.editorComponentTypes.sendEmailOTP]: constants.componentTypes.sendEmailOTP,
  [constants.editorComponentTypes.sessionExists]: constants.componentTypes.sessionExists,
  [constants.editorComponentTypes.oidcIdP]: constants.componentTypes.oidcIdP,
  [constants.editorComponentTypes.samlIdP]: constants.componentTypes.samlIdP,
  [constants.editorComponentTypes.validationError]: constants.componentTypes.validationError,
  [constants.editorComponentTypes.setClaims]: constants.componentTypes.setClaims
};
export const addComponentType = (type: string) => { nodeComponentTypes[type] = type };
const getNodeComponentType = (nodeData: { [index: string]: any }) => {
  return nodeComponentTypes[nodeData.componentType];
};

// Convert a object identifying key name from the editor to the corresponding AAD identity key name.
// Editor key names not in the map will be unchanged (they will be assumed to be extension properties).
const convertEditorKeyNameToAADKeyName = (keyName: string) => {
  return constants.customPolicySchema.aad.userKeyIdentifiers.hasOwnProperty(keyName)
    ? (constants.customPolicySchema.aad.userKeyIdentifiers as { [index: string]: string })[keyName]
    : keyName;
};

const sessionActionTypes: { [index: string]: string } = {
  alwaysExecute: 'always',
  skipForSpecificSession: 'flagSession',
  useGlobalSession: 'globalSSO',
};

const restAuthTypes: { [index: string]: string } = {
  open: jsonXMLConstants.restAuthTypes.none.id,
  none: jsonXMLConstants.restAuthTypes.none.id,
  aadBearer: jsonXMLConstants.restAuthTypes.aadBearer.id,
  clientCert: jsonXMLConstants.restAuthTypes.clientCert.id
};

export const parsePasswordComplexity = (pwClaimData: { [index: string]: any }) => {
  let pwComplexity: JE_PasswordComplexityRequirements = {
    lowercaseLetters: pwClaimData.options.includes('Lowercase Letters (a to z)'),
    uppercaseLetters: pwClaimData.options.includes('Uppercase Letters (A to Z)'),
    letters: pwClaimData.options.includes('Letters (a to z and A to Z)'),
    numbers: pwClaimData.options.includes('Number (0-9)'),
    specialCharacters: pwClaimData.options.includes('Special character($./!)'),
    numberIncluded: pwClaimData.minConstraints ? parseInt(pwClaimData.minConstraints) : undefined,
  };
  if (pwClaimData.options.includes('length')) {
    pwComplexity.minimumLength = pwClaimData.length[0] ? parseInt(pwClaimData.length[0]) : undefined;
    pwComplexity.maximumLength = pwClaimData.length[1] ? parseInt(pwClaimData.length[1]) : undefined;
  }
  return pwComplexity;
};


const extractOtherFCNData: (
  nodeData: any,
  componentType: string,
  buildSettings: BuildSettings,
  linkType?: string,
) => JE_NodeData = (nodeData: any, componentType: string, buildSettings: BuildSettings, linkType?: string) => {
  let claimAttributeMap: JE_AAD_ClaimMapping[];
  let objectIndexAttribute: string;
  let inputConcatenations: [string, string][] = [];
  let contentDefinitionFieldValue: string;
  switch (componentType) {
    case constants.componentTypes.signIn:
      let linkCollection: { [index: string]: any } = {};
      Object.keys(nodeData.links).forEach((linkId: string) => {
        if (linkId === 'socialProviders') {
          let linkCol: { [index: string]: boolean } = {};
          Object.keys(nodeData.links.socialProviders).forEach((socialLinkId: string) => {
            linkCol[socialLinkId] = parseToBoolean(nodeData.links.socialProviders[socialLinkId]);
          });
          linkCollection.socialProviders = linkCol;
        } else {
          linkCollection[linkId] = parseToBoolean(nodeData.links[linkId]);
        }
      });
      if (nodeData.nodeLinks) {
        linkCollection.customLinks = [];
        nodeData.nodeLinks.forEach((linkId: string) => {
          linkCollection.customLinks.push(linkId);
        });
      }
      contentDefinitionFieldValue = extractTemplatizableField(nodeData.contentDefinition, buildSettings);
      return {
        userNameType: nodeData.usernameType,
        keepMeSignedIn: parseToBoolean(nodeData.keepMeSignedIn),
        links: linkCollection as JE_SignIn_LinkCollection,
        linkCount: Object.keys(linkCollection).reduce((previous, current) => {
          if (current === 'socialProviders') {
            return (
              previous +
              Object.keys(linkCollection.socialProviders).reduce(
                (previous, current) => previous + (linkCollection.socialProviders[current] ? 1 : 0),
                0,
              )
            );
          } else if (current === 'customLinks') {
            return previous + (linkCollection[current] ? linkCollection[current].length : 0);
          } else {
            return previous + (linkCollection[current] ? 1 : 0);
          }
        }, 1),
        collectPassword: parseToBoolean(nodeData.showPasswordTextbox),
        userNameClaim: nodeData.username,
        generateLandingCEX: true,
        contentDefinitionUrl: contentDefinitionFieldValue ? contentDefinitionFieldValue : '',
        formSubmitAction: nodeData.formSubmitAction?.id ? nodeData.formSubmitAction.id : undefined,
        validationErrorMessage: nodeData.validationErrorMessage ? nodeData.validationErrorMessage : undefined
      };
    case constants.componentTypes.aadRead:
      claimAttributeMap = nodeData.attributes.map((mca: { [index: string]: any }) => {
        return { claimId: mca.claim, attributeId: mca.id, defaultValue: mca.defaultValue };
      });
      objectIndexAttribute = convertEditorKeyNameToAADKeyName(nodeData.indexProperty.id);
      claimAttributeMap.push({ claimId: nodeData.indexProperty.claim, attributeId: objectIndexAttribute });
      return {
        objectIndex: objectIndexAttribute,
        objectIndexAttribute: { claimId: nodeData.indexProperty.claim, attributeId: objectIndexAttribute },
        claimAttributeMap: claimAttributeMap,
        throwErrorIfNotFound:
          nodeData.throwErrorIfNotFound !== undefined ? parseToBoolean(nodeData.throwErrorIfNotFound) : false, // Default to true for AAD read
        errorMessage: nodeData.errorMessage,
      };
    case constants.componentTypes.aadWrite:
      claimAttributeMap = [];
      let isCreateNew = nodeData.componentType === constants.editorComponentTypes.aadCreate;
      nodeData.attributes.forEach((mca: { [index: string]: any }) => {
        if (mca.claimOrFunc === 'Function') {
          if (mca.transformation === 'string concatenation') {
            inputConcatenations.push([mca.claim, mca.claim2]);
            claimAttributeMap.push({
              claimId: '',
              attributeId: mca.id,
              defaultValue: mca.defaultValue,
              collectionOp: mca.operation,
              concatIndex: inputConcatenations.length - 1,
            });
          } else {
            throw `Unknown AAD claim mapping function: ${mca.transformation}`;
          }
        } else if (mca.claimOrFunc === 'Random') {
          if (mca.id !== 'password' || !isCreateNew) {
            throw 'Illegal AAD claim mapping - random generation only allowed for passwords in AAD create operations';
          } else {
            claimAttributeMap.push({
              attributeId: mca.id,
              claimId: '',
            });
          }
        } else if (mca.claimOrFunc === 'Session Context') {
          if (isRealResolver(mca.resolver)) {
            claimAttributeMap.push({
              claimId: mca.claim,
              attributeId: mca.id,
              defaultValue: mca.resolver,
              alwaysUseDefaultValue: true,
              claimResolver: true,
            });
          } else if (isSpecialResolver(mca.resolver)) {
            claimAttributeMap.push({
              claimId: mca.claim,
              attributeId: mca.id,
              defaultValue: mca.resolver,
              alwaysUseDefaultValue: true,
              specialSessionContext: true,
            });
          }
        } else {
          claimAttributeMap.push({
            claimId: mca.claim,
            attributeId: mca.id,
            defaultValue: mca.defaultValue,
            alwaysUseDefaultValue: mca.alwaysUseDefaultValue ? parseToBoolean(mca.alwaysUseDefaultValue) : undefined,
            collectionOp: collectionOpNames.hasOwnProperty(mca.operation)
              ? collectionOpNames[mca.operation as keyof typeof collectionOpNames]
              : undefined,
          });
        }
      });
      objectIndexAttribute = convertEditorKeyNameToAADKeyName(nodeData.indexProperty.id);
      claimAttributeMap.push({ claimId: nodeData.indexProperty.claim, attributeId: objectIndexAttribute });
      return {
        objectIndex: objectIndexAttribute,
        claimAttributeMap: claimAttributeMap,
        throwErrorIfExists:
          nodeData.throwErrorIfExists !== undefined
            ? parseToBoolean(nodeData.throwErrorIfExists)
            : nodeData.componentType === constants.editorComponentTypes.aadCreate,
        throwErrorIfNotFound:
          nodeData.throwErrorIfNotFound !== undefined
            ? parseToBoolean(nodeData.throwErrorIfNotFound)
            : nodeData.componentType !== constants.editorComponentTypes.aadCreate,
        errorMessage: nodeData.errorMessage
          ? nodeData.errorMessage
          : nodeData.throwErrorIfNotFound !== undefined
            ? 'User not found'
            : 'User already exists',
        isCreateNew: isCreateNew,
        inputConcatenations: inputConcatenations,
        objectIdOutputClaim: nodeData.mapObjectId ? nodeData.mappedClaim : undefined,
        upnOutputClaim: nodeData.mapUserPrincipalName ? nodeData.upnClaim : undefined
      };
    case constants.componentTypes.appInsights:
      return {
        inputFields: nodeData.claims,
        instrumentationKey: extractTemplatizableField(nodeData.instrumentationKey, buildSettings),
        eventType: nodeData.eventType,
      };
    case constants.componentTypes.userInput:
      let emailValidationField: JE_UserInput_EmailValidation | null = null;
      if (nodeData.validateEmail) {
        if (nodeData.email?.valEmailType === 'newEmail') {
          emailValidationField = {
            emailClaimId: nodeData.email.newEmail.attributeSource,
            prefillValue: parseToBoolean(nodeData.email.newEmail.prefillValue),
            prefillSource: parseToBoolean(nodeData.email.newEmail.prefillValue)
              ? nodeData.email.newEmail.attributeSource
              : undefined,
            prefillSourceType: parseToBoolean(nodeData.email.newEmail.prefillValue) ? 'claim' : 'none',
            readonly: false,
            expiryTime: nodeData.email.expiryTime,
            sendAction: nodeData.emailVerificationSendAction?.id ? nodeData.emailVerificationSendAction.id : undefined,
          };
        } else if (nodeData.email?.valEmailType === 'valPrevEmail') {
          emailValidationField = {
            emailClaimId: nodeData.email.valPrevEmail.emailAttribute,
            prefillValue: true,
            prefillSource: nodeData.email.valPrevEmail.emailSource
              ? nodeData.email.valPrevEmail.emailSource
              : nodeData.email.valPrevEmail.emailAttribute,
            prefillSourceType: nodeData.email.valPrevEmail.sourceType
              ? nodeData.email.valPrevEmail.sourceType
              : 'claim',
            readonly: !parseToBoolean(nodeData.email.valPrevEmail.editEmail),
            expiryTime: nodeData.email.expiryTime,
            sendAction: nodeData.emailVerificationSendAction?.id ? nodeData.emailVerificationSendAction.id : undefined,
          };
        }
      }
      let inputFields: JE_UserInput_InputField[] = [];
      let specialPasswordReplacement = false;
      nodeData.attributes.forEach((nodeAttr: { [index: string]: any }) => {
        const prefillType = userInputFieldPrefillValues[nodeAttr.prefillValue as string];
        // TODO: Something cleaner (special case to make repeated passwords behave well with default content defs)
        if ((nodeAttr.repeatField || nodeAttr.enforcePassword) && (nodeAttr.attributeName === 'password')) { // This special-case behavior will need to be fixed up at some point.
          specialPasswordReplacement = true;
          if (nodeAttr.repeatField) {
            inputFields.push({
              claimId: constants.specialClaims.newPassword.id,
              kind: 'data',
              readonly: nodeAttr.readOnly,
              prefilled: prefillType,
              prefill_source:
                prefillType !== 'none'
                  ? prefillType === 'claim'
                    ? nodeAttr.attributeSource
                    : prefillType === 'resolver'
                      ? nodeAttr.sessionContext
                      : nodeAttr.defaultValue
                  : '',
              required: nodeAttr.required
            });
            inputFields.push({
              claimId: constants.specialClaims.reenterPassword.id,
              kind: 'data',
              readonly: nodeAttr.readOnly,
              prefilled: prefillType,
              prefill_source:
                prefillType !== 'none'
                  ? prefillType === 'claim'
                    ? nodeAttr.attributeSource
                    : prefillType === 'resolver'
                      ? nodeAttr.sessionContext
                      : nodeAttr.defaultValue
                  : '',
              required: nodeAttr.required
            });
          } else {
            inputFields.push({
              claimId: constants.specialClaims.newPassword.id,
              kind: 'data',
              readonly: nodeAttr.readOnly,
              prefilled: prefillType,
              prefill_source:
                prefillType !== 'none'
                  ? prefillType === 'claim'
                    ? nodeAttr.attributeSource
                    : prefillType === 'resolver'
                      ? nodeAttr.sessionContext
                      : nodeAttr.defaultValue
                  : '',
              required: nodeAttr.required
            });
          }
        } else {
          inputFields.push({
            claimId: nodeAttr.attributeName,
            kind: 'data', // TODO: Should be set based on whether this is a data field, or an email validation, etc.
            readonly: nodeAttr.readOnly,
            prefilled: prefillType,
            prefill_source:
              prefillType !== 'none'
                ? prefillType === 'claim'
                  ? nodeAttr.attributeSource
                  : prefillType === 'resolver'
                    ? nodeAttr.sessionContext
                    : nodeAttr.defaultValue
                : '',
            required: nodeAttr.required
          });
        }
      });
      return {
        inputFields: inputFields,
        emailValidation: emailValidationField,
        specialPasswordValidation: specialPasswordReplacement,
        contentDefinitionUrl: extractTemplatizableField(nodeData.contentDefinition, buildSettings),
        formSubmitAction: nodeData.formSubmitAction?.id ? nodeData.formSubmitAction.id : undefined,
        validationErrorMessage: nodeData.validationErrorMessage ? nodeData.validationErrorMessage : undefined
      };
    case constants.componentTypes.setClaims:
      let setClaimsData: JE_SetClaimsNodeData = {
        claimsToSet: Object.keys(nodeData.setClaims).map((scId: string) => {
          let srcValue = extractTemplatizableField(nodeData.setClaims[scId].sourceValue, buildSettings);
          return {
            claimToSet: nodeData.setClaims[scId].destClaim,
            sourceType: nodeData.setClaims[scId].sourceType === 'appsetting' ? 'literal' : nodeData.setClaims[scId].sourceType,
            sourceValue: srcValue,
            defaultValue: nodeData.setClaims[scId].defaultValue,
            transformData: (nodeData.setClaims[scId].sourceType === 'transform') ?
              {
                transform: srcValue,
                transformData: nodeData.setClaims[scId].transformData,
                getClaims: generateGetClaimsMethod(srcValue, nodeData.setClaims[scId].transformData),
                makeTransform: generateMakeTransformMethod(srcValue, nodeData.setClaims[scId].transformData)
              } : undefined
          }
        })
      }
      return setClaimsData;
    case constants.componentTypes.google:
      let googleNodeData: JE_IdPNodeData = {
        clientSecretRef: settings.useTestSocialIds ? settings.testSocialIDs.google.clientSecretRef : extractTemplatizableField(nodeData.clientSecretContainerId, buildSettings),
        clientId: settings.useTestSocialIds ? settings.testSocialIDs.google.clientId : extractTemplatizableField(nodeData.clientId, buildSettings),
        partnerClaimMappings: nodeData.attributes.map((attrMap: { [index: string]: any }) => {
          let mapping: JE_GenericIdPClaimMapping = { partnerClaimType: attrMap.id, claimId: attrMap.claim };
          return mapping;
        }),
      };
      nodeData.extraOutputClaims.forEach((eoc: any) => {
        googleNodeData.partnerClaimMappings.push({
          partnerClaimType: '',
          claimId: eoc.claimId,
          defaultValue: eoc.defaultValue,
          alwaysUseDefaultValue: true
        });
      })
      return googleNodeData;
    case constants.componentTypes.conditional:
      return {
        conditionGroups: nodeData.groups.map((condGroup: { [index: string]: any }[]) => {
          return {
            conditions: condGroup.map((condition: { [index: string]: any }) => {
              return {
                claimSrc: condition.claim,
                operator: condition.operator,
                compareTo: condition.compareTo,
                compareValue: condition.comValue ? condition.comValue : '',
                ignoreCase: condition.option === 'ignorecase',
                dateTimeOffset: condition.dateTimeOffset,
              };
            }),
          };
        }),
      };
    case constants.componentTypes.usernamePasswordValidation:
      return {
        userIdType: 'username',
        userIdClaim: nodeData.username,
        passwordClaim: 'password',
        objectIdClaim: nodeData.claim,
        clientId: extractSettingFieldValue('proxyIefAppId', buildSettings), // TODO: Get this from the user somehow
        resourceId: extractSettingFieldValue('iefAppId', buildSettings), // TODO: Get this from the user somehow
        errorMessage: nodeData.errorMsg,
      };
    case constants.componentTypes.rest:
      let restData: JE_RESTNodeData = {
        authType: restAuthTypes[nodeData.authenticationType],
        serviceURL: extractTemplatizableField(nodeData.serviceUrl, buildSettings),
        sendClaimsIn: nodeData.sendClaimsIn ? getKeyedValue(nodeData.sendClaimsIn, jsonXMLConstants.customPolicySchema.restApiSendClaimsIn) : jsonXMLConstants.customPolicySchema.restApiSendClaimsIn.body,
        inputParameters: nodeData.parameters
          .filter((param: { [index: string]: any }) => {
            return param.type === 'input';
          })
          .map((param: { [index: string]: any }) => {
            return {
              sourceValue: param.claimName,
              sourceType: !param.sourceType || param.sourceType === 'claim' ? 'claim' : 'resolver',
              partnerClaimType: param.parameterName,
              defaultValue: param.defaultValue,
              alwaysUseDefaultValue: parseToBoolean(param.alwaysUseDefaultValue),
            };
          }),
        outputParameters: nodeData.parameters
          .filter((param: { [index: string]: any }) => {
            return param.type === 'output';
          })
          .map((param: { [index: string]: any }) => {
            return {
              sourceValue: param.claimName,
              sourceType: !param.sourceType || param.sourceType === 'claim' ? 'claim' : 'resolver',
              partnerClaimType: param.parameterName,
              defaultValue: param.defaultValue,
              alwaysUseDefaultValue: parseToBoolean(param.alwaysUseDefaultValue),
            };
          }),
      };
      if (restData.authType === jsonXMLConstants.restAuthTypes.aadBearer.id) {
        restData.clientIdContainerName = extractTemplatizableField(nodeData.clientIdContainer, buildSettings);
        restData.clientSecretContainerName = extractTemplatizableField(nodeData.clientSecretContainer, buildSettings);
        restData.issuerTenant = extractTemplatizableField(nodeData.issuerTenant, buildSettings);
        restData.applicationIdUri = extractTemplatizableField(nodeData.applicationIdUri, buildSettings);
      } else if (restData.authType === jsonXMLConstants.restAuthTypes.clientCert.id) {
        restData.clientCertContainerName = extractTemplatizableField(nodeData.clientCertContainer, buildSettings);
      }
      return restData;
    case constants.componentTypes.mfa:
      let mfaData: JE_MFANodeData = {
        idClaimId: nodeData.idClaim,
        phoneNumberInputClaimId: nodeData.phoneNumberClaim,
        phoneNumberOutputClaimId: nodeData.enteredPhoneNumberClaim,
        numberEntryMode: nodeData.mode,
        newlyEnteredFlagClaimId: nodeData.newNumberFlagClaim,
        contentDefinitionUrl: extractTemplatizableField(nodeData.contentDefinition, buildSettings),
      };
      return mfaData;
    case constants.componentTypes.webAuthnRegistration:
      contentDefinitionFieldValue = extractTemplatizableField(nodeData.contentDefinition, buildSettings)
      let webAuthnRData: JE_WebAuthnRegistrationNodeData = {
        credentialApiUrl: nodeData.apiUrl ? nodeData.apiUrl : settings.defaultWebAuthnMakeCredentialAPIUrl,
        storageApiUrl: settings.defaultWebAuthnStorageAPIUrl,
        idClaim: nodeData.idClaim,
        nameClaim: nodeData.nameClaim,
        displayNameClaim: nodeData.displayNameClaim,
        successClaim: nodeData.successClaim,
        contentDefinitionUrl: contentDefinitionFieldValue ? contentDefinitionFieldValue : settings.webAuthnRegistrationContentDefinitionUri
      };
      return webAuthnRData;
    case constants.componentTypes.webAuthnAuthentication:
      contentDefinitionFieldValue = extractTemplatizableField(nodeData.contentDefinition, buildSettings)
      let webAuthnAData: JE_WebAuthnAuthenticationNodeData = {
        credentialApiUrl: nodeData.apiUrl ? nodeData.apiUrl : settings.defaultWebAuthnAssertionAPIUrl,
        storageApiUrl: settings.defaultWebAuthnStorageAPIUrl,
        idClaim: nodeData.idClaim,
        successClaim: nodeData.successClaim,
        contentDefinitionUrl: contentDefinitionFieldValue ? contentDefinitionFieldValue : settings.webAuthnAuthenticationContentDefinitionUri,
      };
      return webAuthnAData;
    case constants.componentTypes.tokenIssuer:
      let tokIssuerData: JE_TokenIssuerNodeData = {
        tokenIssuerType: nodeData.protocol
          ? nodeData.protocol
          : constants.customPolicySchema.tokenIssuerTypes.oidc.type, // Default to JWT for old templates.
        issuerUri:
          nodeData.protocol === constants.customPolicySchema.tokenIssuerTypes.saml.type
            ? extractTemplatizableField(nodeData.issuerUri, buildSettings)
            : undefined, // Someday when we have integrated customer deployments we'll get this from a field in the UI
        ssoScope: nodeData.ssoScope ? getKeyedTemplatizableValue(nodeData.ssoScope, jsonXMLConstants.ssoSessionScopes, buildSettings) : jsonXMLConstants.ssoSessionScopes.Tenant,
        ssoExpiryType: nodeData.ssoExpiryType ? getKeyedTemplatizableValue(nodeData.ssoExpiryType, jsonXMLConstants.ssoSessionExpiryTypes, buildSettings) : jsonXMLConstants.ssoSessionExpiryTypes.Absolute,
        ssoExpiryTime: nodeData.ssoExpirySeconds ? extractTemplatizableField(nodeData.ssoExpirySeconds, buildSettings) : "900",
        claims: nodeData.attributes.map((attrEntry: { [index: string]: any }) => {
          return {
            claimId: attrEntry.attributeName,
            defaultValue: attrEntry.defaultValue ? attrEntry.defaultValue : undefined,
            defaultValueSource: attrEntry.defaultValueSource ? attrEntry.defaultValueSource : undefined,
            partnerClaimType: attrEntry.attributeName === nodeData.subClaim ? 'sub' : (attrEntry.partnerClaimType ? attrEntry.partnerClaimType : undefined),
          };
        }),
        endpoints: [],
      };
      if (
        (nodeData.protocol ? nodeData.protocol === constants.customPolicySchema.tokenIssuerTypes.oidc.type : true) &&
        nodeData.userInfoEndpoint &&
        nodeData.userInfoEndpoint.id
      ) {
        tokIssuerData.endpoints.push({ id: 'UserInfo', journeyId: nodeData.userInfoEndpoint.id });
      }
      return tokIssuerData;
    case constants.componentTypes.webAuthnCredentialExists:
      contentDefinitionFieldValue = extractTemplatizableField(nodeData.contentDefinition, buildSettings)
      let webAuthnCEData: JE_WebAuthnCredentialExistsNodeData = {
        existsClaim: nodeData.existsClaim,
        contentDefinitionUrl: contentDefinitionFieldValue ? contentDefinitionFieldValue : settings.webAuthnCredentialExistsContentDefinitionUri,
      };
      return webAuthnCEData;
    case constants.componentTypes.webAuthnAvailability:
      contentDefinitionFieldValue = extractTemplatizableField(nodeData.contentDefinition, buildSettings)
      let webAuthnAVData: JE_WebAuthnAvailabilityNodeData = {
        availabilityClaim: nodeData.availabilityClaim,
        requirePlatformAuth: nodeData.requirePlatformAuth,
        contentDefinitionUrl: contentDefinitionFieldValue ? contentDefinitionFieldValue : settings.webAuthnAvailabilityContentDefinitionUri,
      };
      return webAuthnAVData;
    case constants.componentTypes.conditionalAccessEvaluation:
      let caeData: JE_ConditionalAccessEvaluationNodeData = {
        idClaim: nodeData.idClaim,
        mfaRegisteredClaim: nodeData.mfaRegisteredClaim,
        changePasswordFlagClaim: nodeData.changePasswordChallenge,
        mfaFlagClaim: nodeData.mfaChallenge,
        blockFlagClaim: nodeData.blockChallenge,
      };
      return caeData;
    case constants.componentTypes.errorPage:
      let eData: JE_ErrorPageNodeData = {
        entries: nodeData.entries.map((e: any) => {
          return {
            sourceType: e.sourceType,
            source: e.source,
          };
        }),
        contentDefinitionUrl: extractTemplatizableField(nodeData.contentDefinition, buildSettings) ?
          extractTemplatizableField(nodeData.contentDefinition, buildSettings) : '',
      };
      return eData;
    case constants.componentTypes.authorizeToken:
      let atData: JE_AuthorizeTokenNodeData = {
        claims: nodeData.claims.map((c: any) => {
          return {
            claimId: c.id,
            partnerClaimType: c.partnerClaimType,
          };
        }),
        issuer: `https://${extractSettingFieldValue('tenantName', buildSettings)}.b2clogin.com/${extractSettingFieldValue('tenantId', buildSettings)}/v2.0/`,
        audience: [extractTemplatizableField(nodeData.oidcApplicationId, buildSettings)],
        issuerSecret: extractSettingFieldValue(jsonConstants.defaultAppSettingFields.tokenSigningKeyContainer.id, buildSettings),
      };
      return atData;
    case constants.componentTypes.userInfoIssuer:
      let uiiData: JE_UserInfoIssuerNodeData = {
        claims: nodeData.attributes.map((c: any) => {
          return {
            claimId: c.attributeName,
            defaultValue: c.defaultValue ? c.defaultValue : undefined,
          };
        }),
        issuerSecret: extractSettingFieldValue(jsonConstants.defaultAppSettingFields.tokenSigningKeyContainer.id, buildSettings),
      };
      return uiiData;
    case constants.componentTypes.sendEmailOTP:
      let seoData: JE_SendEmailOTPNodeData = {
        otpField: nodeData.otpField,
        emailClaim: nodeData.emailClaim,
        templateName: nodeData.emailTemplate,
        language: {
          sourceType: nodeData.language.sourceType,
          source: nodeData.language.source,
        },
        fields: nodeData.fields.map((fld: any) => {
          return {
            sourceType: fld.sourceType,
            source: fld.source,
            templateField: fld.templateField,
          };
        }),
      };
      return seoData;
    case constants.componentTypes.sessionExists:
      return {};
    case constants.componentTypes.validationError:
      return {};
    case constants.componentTypes.oidcIdP:
      let oidcData: JE_OIDCIdPNodeData = {
        idpDomain: extractTemplatizableField(nodeData.idpDomain, buildSettings),
        metadataUrl: extractTemplatizableField(nodeData.metadataUrl, buildSettings),
        authEndpointUrl: extractTemplatizableField(nodeData.authEndpointUrl, buildSettings),
        endSessionEndpointUrl: extractTemplatizableField(nodeData.endSessionEndpointUrl, buildSettings),
        clientId: extractTemplatizableField(nodeData.clientId, buildSettings),
        responseType: nodeData.responseType,
        clientSecretContainerName: extractTemplatizableField(nodeData.clientSecretContainerName, buildSettings),
        responseMode: nodeData.responseMode,
        inputClaims: nodeData.inputClaims.map((ic: any) => {
          return { claimId: ic.claimId, partnerClaimType: ic.partnerClaimType };
        }),
        issuerIdPartnerClaimType: nodeData.issuerIdPartnerClaimType,
        outputClaims: nodeData.outputClaims.map((oc: any) => {
          return { claimId: oc.claimId, partnerClaimType: oc.partnerClaimType, defaultValue: oc.defaultValue, alwaysUseDefaultValue: oc.alwaysUseDefaultValue };
        }),
        forceScopes: []
      };
      return oidcData;
    case constants.componentTypes.samlIdP:
      let samlData: JE_SAMLIdPNodeData = {
        idpDomain: extractTemplatizableField(nodeData.idpDomain, buildSettings),
        metadataUrl: extractTemplatizableField(nodeData.metadataUrl, buildSettings),
        signRequests: nodeData.signRequests,
        messageSigningKey: extractTemplatizableField(nodeData.messageSigningKey, buildSettings),
        signatureAlgorithm: nodeData.signatureAlgorithm,
        expectResponseSignatures: nodeData.responseSignatures,
        encryptAssertions: nodeData.assertionsEncrypted,
        assertionDecryptionKey: extractTemplatizableField(nodeData.assertionDecryptionKey, buildSettings),
        metadataSigningKey: extractTemplatizableField(nodeData.metadataSigningKey, buildSettings),
        requestUserClaim: nodeData.requestUser ? nodeData.requestUserClaim : undefined,
        issuerIdPartnerClaimType: nodeData.issuerIdPartnerClaimType,
        outputClaims: nodeData.outputClaims.map((oc: any) => {
          return { claimId: oc.claimId, partnerClaimType: oc.partnerClaimType, defaultValue: oc.defaultValue, alwaysUseDefaultValue: oc.alwaysUseDefaultValue };
        })
      };
      return samlData;
    default:
      return extractFCNData(
        nodeData,
        componentType,
        buildSettings,
        linkType
      );
  }
};

const extractSessionBehavior = (nodeType: string, sessionData: any) => {
  if (nodeType === constants.componentTypes.conditional) {
    return { sessionAction: 'always', sessionId: '' };
  } else if (nodeType === constants.componentTypes.appInsights) {
    return { sessionAction: 'always', sessionId: '' };
  } else {
    if (sessionData) {
      return { sessionAction: sessionActionTypes[sessionData.option], sessionId: sessionData.sessionId };
    } else {
      return { sessionAction: 'always', sessionId: '' };
    }
  }
};

const makeFlowchartNode: (
  nodeId: string,
  nodeName: string,
  nodeData: any,
  buildSettings: BuildSettings,
) => JsonFlowchartNode = (nodeId: string, nodeName: string, nodeData: any, buildSettings: BuildSettings) => {
  if (!nodeComponentTypes.hasOwnProperty(nodeData.componentType)) {
    throw `Unknown component type '${nodeData.componentType}'`;
  }
  return {
    nodeId: nodeId,
    displayName: nodeName ? nodeName : nodeData.componentType,
    sanitizedDisplayName: nodeName ? sanitizeDisplayName(nodeName) : sanitizeDisplayName(nodeData.componentType),
    kind: getNodeComponentType(nodeData),
    isLink: nodeData.linkId ? true : false,
    linkType: nodeData.linkId
      ? nodeData.linkType === 'localAccountSignIn'
        ? 'localAccount'
        : nodeData.linkType
      : undefined,
    validations: nodeData.formSubmitAction ? nodeData.formSubmitAction.id : undefined,
    children: [],
    parents: [],
    data: extractOtherFCNData(nodeData, getNodeComponentType(nodeData), buildSettings, nodeData.linkType),
    childBranchVars: [],
    sessionBehavior: extractSessionBehavior(getNodeComponentType(nodeData), nodeData.sessionManagement),
  };
};

// Turns node/edge collections into nodes with parent/child IDs.
export const convertFlowchartDataToGraph = (
  flowcharts: { [id: string]: any },
  nodeData: { [id: string]: any },
  buildSettings: BuildSettings
) => {
  let flowchartIndices: { [flowId: string]: JE_FlowchartIndex } = {};
  let nodes: { [nodeId: string]: JsonFlowchartNode } = {};
  let usedNodeIds: Set<string> = new Set();
  Object.keys(nodeData).forEach((nid) => {
    usedNodeIds.add(nid);
  });
  let instrumentCounter = 0;
  Object.keys(flowcharts).forEach((flowchartId: string) => {
    let fNodes: { [nodeId: string]: JsonFlowchartNode } = {};
    if (flowcharts[flowchartId].nodes.length > 0) {
      let errorPageNodeIds: string[] = [];
      let tokenIssuerId: string = '';
      flowcharts[flowchartId].nodes.forEach((fcNode: any) => {
        fNodes[fcNode.id] = makeFlowchartNode(fcNode.id, fcNode.data?.label, nodeData[fcNode.id], buildSettings);
        if (settings.internallyLinkErrorPagesToTokenIssuer) {
          if ([constants.componentTypes.errorPage, constants.componentTypes.validationError].includes(fNodes[fcNode.id].kind)) {
            errorPageNodeIds.push(fcNode.id);
          } else if (fNodes[fcNode.id].kind === constants.componentTypes.tokenIssuer) {
            tokenIssuerId = fcNode.id;
          }
        }
      });
      if (settings.internallyLinkErrorPagesToTokenIssuer) {
        // Error pages are dead-ends in UI and at runtime but structurally should be considered linked directly to token issuer.
        if (tokenIssuerId) {
          errorPageNodeIds.forEach((eid) => {
            fNodes[eid].children.push(tokenIssuerId);
            fNodes[tokenIssuerId].parents.push(eid);
          });
        }
      }

      flowcharts[flowchartId].edges.forEach((edge: any) => {
        if (
          !fNodes[edge.source].children.includes(edge.target) &&
          !fNodes[edge.source].children
            .filter((cid) => {
              return fNodes[cid].kind === constants.componentTypes.policyInstrumentation;
            })
            .some((pic) => {
              return fNodes[pic].children.includes(edge.target);
            })
        ) {
          let targetNode = edge.target;
          if (
            settings.enablePolicyWideInstrumentation &&
            fNodes[edge.source].kind !== constants.componentTypes.signIn
          ) {
            let instNodeId = getNextValidID(`PolicyInstrumentation_${instrumentCounter}`, usedNodeIds);
            usedNodeIds.add(instNodeId);
            let instNode: JsonFlowchartNode = {
              nodeId: instNodeId,
              displayName: `Policy Instrumentation ${instrumentCounter}`,
              sanitizedDisplayName: `Policy_Instrumentation_${instrumentCounter}`,
              kind: constants.componentTypes.policyInstrumentation,
              isLink: false,
              data: {},
              children: [edge.target],
              parents: [],
              childBranchVars: [],
              sessionBehavior: undefined,
            };
            fNodes[instNodeId] = instNode;
            instrumentCounter += 1;
            fNodes[edge.target].parents.push(instNodeId);
            targetNode = instNodeId;
          }
          if (edge.sourceHandle === 'down') {
            // Conditional node true branch; child order matters
            fNodes[edge.source].children.unshift(targetNode);
          } else {
            fNodes[edge.source].children.push(targetNode);
          }
          fNodes[targetNode].parents.push(edge.source);
        }
      });
      if (settings.enablePolicyWideInstrumentation) {
        let potentialTailNodes = Object.keys(fNodes).filter((key: string) => fNodes[key].children.length === 0);
        potentialTailNodes.forEach((tnid: string) => {
          if (
            ![
              constants.componentTypes.tokenIssuer,
              constants.componentTypes.errorPage,
              constants.componentTypes.userInfoIssuer,
              constants.componentTypes.validationError
            ].includes(fNodes[tnid].kind)
          ) {
            let instNodeId = getNextValidID(`PolicyInstrumentation_${instrumentCounter}`, usedNodeIds);
            usedNodeIds.add(instNodeId);
            let instNode: JsonFlowchartNode = {
              nodeId: instNodeId,
              displayName: `Policy Instrumentation ${instrumentCounter}`,
              sanitizedDisplayName: `Policy_Instrumentation_${instrumentCounter}`,
              kind: constants.componentTypes.policyInstrumentation,
              isLink: false,
              data: {},
              children: [],
              parents: [tnid],
              childBranchVars: [],
              sessionBehavior: undefined,
            };
            fNodes[instNodeId] = instNode;
            instrumentCounter += 1;
            fNodes[tnid].children.push(instNodeId);
          }
        });
      }
      // Traverse and discard nodes not connected to the root.
      // TODO: Need the JSON from the editor to indicate the root. In the meantime, assume the first node found with no parents is the root.
      let potentialRootKeys = Object.keys(fNodes).filter((key: string) => fNodes[key].parents.length == 0);
      if (potentialRootKeys.length < 1) {
        throw 'No root found for flowchart nodes - a cycle is likely';
      }
      flowchartIndices[flowchartId] = {
        nodes: fNodes,
        root: potentialRootKeys[0],
        type: flowcharts[flowchartId].flowchartType,
        name: flowcharts[flowchartId].flowchartName,
      };
      if (flowcharts[flowchartId].parentFCId) {
        flowchartIndices[flowchartId].parentFlow = flowcharts[flowchartId].parentFCId;
      }
      Object.keys(fNodes).forEach((nodeId: string) => {
        nodes[nodeId] = fNodes[nodeId];
      });
    }
  });
  // There is a frontend bug involving changes to the sign-in name claim type after UPV components in the submit action already exist,
  // in which the JSON editor nodeData state does not automatically update the sign-in name claim without manually saving the node.
  // The below block of code should now be unnecessary but is currently preventing that bug from breaking expected behavior.
  Object.keys(nodes).forEach((nodeId: string) => {
    if (
      nodes[nodeId].kind === constants.componentTypes.signIn &&
      (nodes[nodeId].data as JE_SignInNodeData).formSubmitAction
    ) {
      let nodeData = nodes[nodeId].data as JE_SignInNodeData;
      Object.keys(flowchartIndices[(nodes[nodeId].data as JE_SignInNodeData).formSubmitAction!].nodes).forEach(
        (vNodeId: string) => {
          if (nodes[vNodeId].kind === constants.componentTypes.usernamePasswordValidation) {
            (nodes[vNodeId].data as JE_UsernamePasswordValidationNodeData).userIdClaim = nodeData.userNameClaim;
          }
        },
      );
    }
  });
  return { flowchartIndices, nodes };
};
